<i18n src="@/locales/models/crop_field_certification.yml" lang="yaml"></i18n>
<i18n
  src="@/locales/views/organization/certifications/basic_certifications_producers/new_crop_field_certification.yml"
  lang="yaml"
></i18n>

<template>
  <go-back-link
    :text="$t('labels.go_back_link')"
    :name="routeName('show_producer')"
    :params="{
      organization_id: organization_id,
      producer_id: producer_id,
    }"
  />

  <bread-crumbs :labels="[module_name, producer?.name]"></bread-crumbs>

  <producer-info v-if="load_general_data" :producer="producer"></producer-info>

  <alert-danger :errors_data="std_err" />

  <div v-if="load_general_data" class="mt-8">
    <div class="panel">
      <div class="panel-header">
        <h3 class="panel-header-title">
          {{ $t("labels.title") }}
        </h3>
      </div>

      <div class="panel-body">
        <div class="space-y-8 divide-y divide-gray-200">
          <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.crop_field_certification.certification_id") }}
              </label>
              <div class="mt-1">
                <select-form
                  v-if="certifications.length > 1"
                  :options="certifications"
                  v-model="certification_id"
                  :class="isInvalidSelect('certification')"
                />
                <p v-else-if="certifications.length == 1">
                  {{ certifications[0].name }}
                </p>
              </div>
            </div>
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.crop_field_certification.crop_field_id") }}
              </label>
              <div class="mt-1">
                <select-form
                  v-if="crop_fields.length > 1"
                  :options="crop_fields"
                  v-model="crop_field_id"
                  :class="isInvalidSelect('crop_field')"
                />
                <p v-else-if="crop_fields.length == 1">
                  {{ crop_fields[0].name }}
                </p>
              </div>
            </div>
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.crop_field_certification.renewal_date") }}
              </label>
              <div class="mt-1">
                <date-form v-model="renewal_date" />
              </div>
            </div>
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.crop_field_certification.expiration_date") }}
              </label>
              <div class="mt-1">
                <date-form
                  v-model="expiration_date"
                  :class="isInvalid('expiration_date')"
                />
              </div>
            </div>
            <div class="sm:col-span-3">
              <div class="mt-1">
                <switch-form
                  v-model="active"
                  :label="$t('model.crop_field_certification.active')"
                />
              </div>
              <div class="mt-1">
                <switch-form
                  v-model="suspended"
                  :label="$t('model.crop_field_certification.suspended')"
                />
              </div>
            </div>
            <div class="sm:col-span-3" v-if="suspended">
              <label class="label-form">
                {{ $t("model.crop_field_certification.suspended_until") }}
              </label>
              <div class="mt-1">
                <date-form
                  v-model="suspended_until"
                  :class="isInvalid('suspended_until')"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="panel-footer">
        <div class="panel-footer-actions">
          <white-button
            @click.prevent="goToShowProducer()"
            :label="$t('labels.cancel_button')"
          />
          <span class="space-actions"></span>
          <success-button
            v-if="!proc_save_button"
            @click.prevent="create()"
            :label="$t('labels.save_button')"
          />
          <success-button-processing v-else :label="$t('labels.save_button')" />
        </div>
      </div>
    </div>
  </div>
  <loading-message v-else />
</template>

<script>
import Requester from "@/views/organization/requesters/certifications/basic_certifications_producers/manage_crop_field_certification.js";
import ProducerInfo from "@/components/views/organization/certifications/basic_certifications_producers/ProducerInfo.vue";

export default {
  name: "NewPackingHouseCertification",
  components: { ProducerInfo },
  data() {
    return {
      load_general_data: false,
      producer: {},
      proc_save_button: false,
      certifications: [],
      certification_id: null,
      crop_field_id: null,
      expiration_date: null,
      renewal_date: null,
      active: true,
      suspended: false,
      suspended_until: null,
      crop_fields: [],
    };
  },
  props: {
    organization_id: {},
    producer_id: {},
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          organization_id: this.organization_id,
        },
      }).then(() => this.loadData())
    );
  },
  methods: {
    loadData: async function () {
      await this.fetchProducer();
      await this.fetchCertifications();
      await this.fetchCropFields();
      this.load_general_data = true;
    },
    fetchProducer: async function () {
      let response = await Requester.fetchProducer(
        this.organization_id,
        this.producer_id
      );

      this.producer = response.data;
    },
    fetchCertifications: async function () {
      let response = await Requester.fetchCertifications(
        this.organization_id,
        this.producer_id
      );

      this.certifications = response.data;
      if (this.certifications.length == 1)
        this.certification_id = this.certifications[0].id;
    },
    fetchCropFields: async function () {
      let response = await Requester.fetchCropFields(
        this.organization_id,
        this.producer_id
      );

      this.crop_fields = response.data;
      if (this.crop_fields.length == 1)
        this.crop_field_id = this.crop_fields[0].id;
    },
    create: async function () {
      this.proc_save_button = true;
      try {
        let response = await Requester.create(
          this.organization_id,
          this.producer_id,
          {
            certification_id: this.certification_id,
            crop_field_id: this.crop_field_id,
            expiration_date: this.expiration_date,
            renewal_date: this.renewal_date,
            active: this.active,
            suspended: this.suspended,
            suspended_until: this.suspended_until,
          }
        );
        this.registerSuccessNotification({
          title: response.data.success_message,
        });
        this.goToShowProducer();
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields,
          });
        }
      }
      this.proc_save_button = false;
    },
    goToShowProducer() {
      this.$router.push({
        name: this.routeName("show_producer"),
        params: {
          organization_id: this.organization_id,
          producer_id: this.producer_id,
        },
      });
    },
  },
};
</script>
